import {
  BIRDModelController,
  PositionedModelController,
  VisualElementController,
} from '@sas-dvr/ltjs-bird';
import { ClosetObject, TypeId } from '@sas-dvr/ltjs-commons';
import { ltjsModule, typeMap } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { BirdEpicDependencies } from '../epics/BirdEpic';
import { loadContainerController } from './loadContainer';
import { loadGraphElementController } from './loadGraphElement';
import { loadMediaContainerController } from './loadMediaContainer';
import { loadPositionedModelOwnerController } from './loadPositionedModelOwner';
import { loadPromptElementController } from './loadPromptElement';
import { loadReportController } from './loadReport';
import { loadSectionController } from './loadSection';
import { loadVisualController } from './loadVisual';
import { loadVisualElementController } from './loadVisualElement';
import { loadImageElementController } from './loadImage';
import { loadTextElementController } from './loadTextElement';
import { loadTableElementController } from './loadTableElement';
import { loadCrosstabElementController } from './loadCrosstabElement';
import { loadWebContentElementController } from './loadWebContentElement';
import { AnyAction } from 'redux';
import { loadKPIElementController } from './loadKPIElement';
import { loadCompositeContainerElementController } from './loadCompositeContainerElement';
import { loadVisualContainerElementController } from './loadVisualContainerElement';
import { loadVisualPromptContainerElementController } from './loadVisualPromptContainerElement';
import { loadMapElementController } from './loadMapElement';
import { loadJobContentElementController } from './loadJobContentElement';

class LoaderFactory<ControllerType extends ClosetObject, LoaderType> {
  private _loaders: { [key: string]: LoaderType };

  constructor(loaders: { [key: string]: LoaderType }) {
    this._loaders = loaders;
  }

  private _getLoaderForTypeId(typeId: TypeId): LoaderType | undefined {
    const typeEntry = typeMap.getTypeEntry(typeId);
    if (!typeEntry) {
      return undefined;
    }

    // let loader = this._loaderMap.get(typeId);
    let loader: LoaderType | undefined = this._loaders[typeEntry.moduleName];

    if (!loader) {
      loader = this._getLoaderForTypeId(typeEntry.parentTypeId);
    }

    return loader;
  }

  public getLoader(controller: ControllerType): LoaderType {
    const loader = this._getLoaderForTypeId(ltjsModule.getTypeId(controller.getClosetAddress()));
    if (!loader) {
      throw new Error(`Unable to find loader for controller ${controller}`);
    }
    return loader;
  }
}

export type BIRDModelControllerLoader = (
  controller: BIRDModelController,
  unload$: Observable<BIRDModelController>,
  myActions$: Observable<AnyAction>,
  state$: StateObservable<ReportState>,
  deps: BirdEpicDependencies
) => Observable<AnyAction>;

export type VisualElementControllerLoader<T = undefined> = (
  visualController: PositionedModelController | null,
  visualElementController: VisualElementController,
  unload$: Observable<BIRDModelController>,
  myActions$: Observable<AnyAction>,
  state$: StateObservable<ReportState>,
  deps: BirdEpicDependencies,
  lifecyleActions?: { create: () => AnyAction; delete?: () => AnyAction },
  extraDeps?: T
) => Observable<AnyAction>;

const birdLoaderFactory = new LoaderFactory<BIRDModelController, BIRDModelControllerLoader>({
  'BIRD::ReportController': loadReportController,
  'BIRD::SectionController': loadSectionController,
  'BIRD::PositionedModelOwnerController': loadPositionedModelOwnerController,
  'BIRD::VisualController': loadVisualController,
  'BIRD::ContainerController': loadContainerController,
  'BIRD::MediaContainerController': loadMediaContainerController,
});

const visualElementLoaderFactory = new LoaderFactory<
  VisualElementController,
  VisualElementControllerLoader
>({
  'BIRD::VisualElementController': loadVisualElementController,
  'BIRD::PromptElementController': loadPromptElementController,
  'BIRD::RangeSliderController': loadPromptElementController,
  'BIRD::GraphElementController': loadGraphElementController,
  'BIRD::ImageElementController': loadImageElementController,
  'BIRD::TextElementController': loadTextElementController,
  'BIRD::MapElementController': loadMapElementController,
  'BIRD::TableElementController': loadTableElementController,
  'BIRD::CrosstabElementController': loadCrosstabElementController,
  'BIRD::WebContentElementController': loadWebContentElementController,
  'BIRD::JobContentElementController': loadJobContentElementController,
  'BIRD::KPIElementController': loadKPIElementController,
  'BIRD::VisualPromptContainerElementController': loadVisualPromptContainerElementController,
  'BIRD::VisualContainerElementController': loadVisualContainerElementController,
  'BIRD::CompositeContainerElementController': loadCompositeContainerElementController,
});

export function getLoaderForController(controller: BIRDModelController): BIRDModelControllerLoader {
  return birdLoaderFactory.getLoader(controller);
}

export function getLoaderForVisualElementController(
  controller: VisualElementController
): VisualElementControllerLoader {
  return visualElementLoaderFactory.getLoader(controller);
}
